import React, { Fragment, useState, useEffect, memo } from 'react';
import { useTable, useSortBy, useRowSelect, useGlobalFilter, usePagination, useFilters } from 'react-table';
import SearchInput from './SearchInput';
import TableEmptyListMessage from './TableEmptyListMessage';
import { Button, TablePagination, Popover, Checkbox } from '@material-ui/core';
import './ListTable.css';
import { DefaultColumnFilter } from '../filters';
import useTranslation from '../Hooks/useTranslation';
import { IconButton } from '@material-ui/core';
// import { Tooltip } from '@material-ui/core';
// import ClearIcon from '@material-ui/icons/Clear';
import FilterListIcon from '@material-ui/icons/FilterList';
import ViewWeekIcon from '@material-ui/icons/ViewWeek';
import IndeterminateCheckbox from './IndeterminateCheckbox';

const readStoredHiddenColumns = (storageKey) => {
    if (!storageKey) return undefined;
    try {
        const raw = window.localStorage.getItem(storageKey);
        return raw ? JSON.parse(raw) : undefined;
    } catch (e) {
        return undefined;
    }
};

const ListTable = props => {

    const {
        columns,
        data,
        onRowClick,
        request,
        tableClass,
        wrapperClass,
        initSort,
        enableSearch = true,
        enableColumnFilters,
        isSelectionColumn,
        onSelectedRows,
        getRowId,
        expandedRowId,
        expandedRowIds,
        expandedRowContent,
        expandedRowLoading,
        getExpandedRowContent,
        getExpandedRowLoading,
        expandedRowClassName,
        enablePagination = true,
        filtersButtonAlign = 'left',
        columnVisibilityMenu = false,
        columnVisibilityStorageKey,
        getRowClassName,
        // Optional controls rendered in the toolbar row, before search / filters
        toolbarStart,
    } = props;

    const expandedIds = expandedRowIds ?? (expandedRowId != null ? [expandedRowId] : []);

    const [showColumnsFilter, setShowColumnsFilters] = useState(false);
    const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = useState(null);

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable({
            columns,
            data,
            defaultColumn,
            initialState: {
                pageIndex: 0,
                pageSize: enablePagination ? 10 : 10000,
                sortBy: initSort ? initSort : [],
                hiddenColumns: columnVisibilityMenu ? (readStoredHiddenColumns(columnVisibilityStorageKey) || []) : [],
            },
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            hooks.visibleColumns.push((columns) => {
                return isSelectionColumn ? [
                    {
                        id: 'selection',
                        className: 'checkBoxCell',
                        Header: ({getToggleAllRowsSelectedProps}) => (
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} id="notificationsTblCheckBoxAllRows"/>
                        ),
                        Cell: ({row}) => (
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} id="notificationsTblCheckBoxReport"/>
                        ),
                    },
                    ...columns
                ] : columns
            })
        }
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setGlobalFilter,
        gotoPage,
        setPageSize,
        page,
        selectedFlatRows,
        allColumns,
        state: {globalFilter, pageIndex, pageSize, filters, hiddenColumns},
        setAllFilters,
    } = tableInstance;

    const translation = useTranslation();

    useEffect(() => {
        if (onSelectedRows)
            onSelectedRows(selectedFlatRows.map(row => row.original.id))
    }, [selectedFlatRows, onSelectedRows])

    useEffect(() => {
        if (!columnVisibilityMenu || !columnVisibilityStorageKey) return;
        try {
            window.localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(hiddenColumns || []));
        } catch (e) {
            // ignore storage failures
        }
    }, [columnVisibilityMenu, columnVisibilityStorageKey, hiddenColumns]);

    const toggleableColumns = allColumns.filter(column => column.id !== 'selection');

    const renderColumnsMenu = () => (
        <Popover
            open={Boolean(columnsMenuAnchorEl)}
            anchorEl={columnsMenuAnchorEl}
            onClose={() => setColumnsMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: filtersButtonAlign === 'right' ? 'right' : 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: filtersButtonAlign === 'right' ? 'right' : 'left' }}
        >
            <div className="listTableColumnsMenu">
                <div className="listTableColumnsMenuHeader">
                    <span className="listTableColumnsMenuTitle">{translation.columnsLabel || 'Columns'}</span>
                    <Button
                        size="small"
                        className="listTableColumnsMenuToggleAll"
                        onClick={() => {
                            const allVisible = toggleableColumns.every(c => c.isVisible);
                            toggleableColumns.forEach(column => column.toggleHidden(allVisible));
                        }}
                    >
                        {toggleableColumns.every(c => c.isVisible) ? (translation.hideAllLabel || 'Hide all') : (translation.showAllLabel || 'Show all')}
                    </Button>
                </div>
                <div className="listTableColumnsMenuList">
                    {
                        toggleableColumns.map(column => (
                            <label key={column.id} className="listTableColumnsMenuItem">
                                <Checkbox
                                    size="small"
                                    color="default"
                                    checked={column.isVisible}
                                    onChange={() => column.toggleHidden(column.isVisible)}
                                />
                                <span>{typeof column.Header === 'string' ? column.Header : column.id}</span>
                            </label>
                        ))
                    }
                </div>
            </div>
        </Popover>
    );

    const renderFiltersButton = () => {
        if (!enableColumnFilters) return null;

        if (columnVisibilityMenu) {
            return (
                <Fragment>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={(e) => setColumnsMenuAnchorEl(e.currentTarget)}
                        startIcon={<ViewWeekIcon style={{ fontSize: 14 }} />}
                        className={`listTableColumnsToggleBtn ${filtersButtonAlign === 'right' ? '' : 'ml10'}`}
                    >
                        <span className='hideTitleRefreshBtn'>{translation.columnsLabel || 'Columns'}</span>
                    </Button>
                    {renderColumnsMenu()}
                </Fragment>
            );
        }

        return (
            <Fragment>
                <Button
                    onClick={() => setShowColumnsFilters((v) => {
                        if (v) setAllFilters([]);
                        return !v;
                    })}
                    startIcon={<FilterListIcon />}
                    className={filtersButtonAlign === 'right' ? '' : 'ml15'}
                >
                    {
                        showColumnsFilter ? <span className='hideTitleRefreshBtn'>{translation.hideFiltersLabel}</span> : <span className='hideTitleRefreshBtn'>{translation.showFiltersLabel}</span>
                    }
                </Button>
                {
                    showColumnsFilter && (
                        <span className='hideTitleRefreshBtn'>
                            <IconButton
                            size="small"
                            disabled={!filters.length}
                            onClick={() => setAllFilters([])}
                        >
                            {/* <Tooltip title={translation.clearAllFiltersLabel}>
                                <ClearIcon />
                            </Tooltip> */}
                        </IconButton>
                        </span>
                    )
                }
            </Fragment>
        );
    };

    const renderSearchBlock = () => {
        if (!enableSearch && !enableColumnFilters && !toolbarStart) return null;
        const alignRight = filtersButtonAlign === 'right';
        return (
            <div className="pageBtnSection pt0 mt5">
                <div className="searchHolder" style={alignRight ? { display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' } : undefined}>
                <div
                    className={`inputHolder${toolbarStart ? ' listTableToolbarStart' : ''}`}
                    style={{display: 'flex', verticalAlign: 'center'}}
                >
                    {toolbarStart}
                    {enableSearch && (
                        <SearchInput
                            search={globalFilter}
                            onChange={setGlobalFilter}
                        />
                    )}

                    {!alignRight && renderFiltersButton()}
                </div>
                <div className="inputHolder" style={alignRight ? { display: 'flex', alignItems: 'center' } : undefined}>
                    {alignRight && renderFiltersButton()}
                </div>
            </div>
            </div>
        );
    };

    const renderEmptyList = () => {
        if (request && !request.isSuccess) return null;
        return !rows.length && <TableEmptyListMessage>{translation.activitiesPagePartsEmptyListPlaceholderLabel}</TableEmptyListMessage>;
    };

    const renderPagination = () => {
        if (!enablePagination || !data) return null;

        return (
            <TablePagination
                classes={{
                    root: 'tableControlsHolder',
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={data.length}
                page={pageIndex}
                SelectProps={{
                    inputProps: {'aria-label': 'rows per page'}
                }}
                onPageChange={(event, page) => {
                    gotoPage(page);
                }}
                rowsPerPage={pageSize}
                onRowsPerPageChange={event => {
                    setPageSize(parseInt(event.target.value));
                }}
            />

        );
    };

    return (
        <Fragment>
            {renderSearchBlock()}
            <div className={wrapperClass || 'tableHolder'}>
                <table {...getTableProps()} className={tableClass || ''}>
                    <thead>
                    {
                        headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} className="headingRow">
                                {
                                    headerGroup.headers.map(column => {
                                        const sortClass = column.isSorted ? (column.isSortedDesc ? 'hasArrow' : 'hasArrow up') : '';
                                        const columnClass = `${column.className} ${sortClass}`;
                                        return (
                                            <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                className={columnClass}
                                            >
                                                {column.render('Header')}
                                                {/*<div>{column.canFilter ? column.render('Filter') : null}</div>*/}
                                            </th>
                                        );
                                    })
                                }
                            </tr>

                        ))
                    }
                    {
                        (enableColumnFilters && showColumnsFilter) && headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} className="headingRow">
                                {
                                    headerGroup.headers.map(column => {
                                        return (
                                            <th {...column.getHeaderProps()}>
                                                {column.canFilter ? column.render('Filter') : null}
                                            </th>
                                        );
                                    })
                                }
                            </tr>

                        ))
                    }
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {renderEmptyList()}
                    {
                        page.map(row => {
                            prepareRow(row);
                            const rowId = getRowId ? getRowId(row) : undefined;
                            const isExpanded = rowId !== undefined && expandedIds.includes(rowId);
                            const columnCount = headerGroups[0]?.headers?.length ?? columns.length;
                            const rowContent = getExpandedRowContent ? getExpandedRowContent(rowId) : expandedRowContent;
                            const rowLoading = getExpandedRowLoading ? getExpandedRowLoading(rowId) : expandedRowLoading;
                            return (
                                <Fragment key={row.id}>
                                    <tr
                                        {...row.getRowProps()}
                                        onClick={() => {
                                            if (onRowClick) {
                                                onRowClick(row.original);
                                            }
                                        }}
                                        style={onRowClick ? { cursor: 'pointer' } : undefined}
                                        className={getRowClassName ? getRowClassName(row.original) : undefined}
                                    >
                                        {
                                            row.cells.map(cell => (
                                                <td
                                                    {...cell.getCellProps({
                                                        className: cell.column.className,
                                                    })}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            ))
                                        }
                                    </tr>
                                    {isExpanded && (
                                        <tr className={expandedRowClassName || undefined}>
                                            <td colSpan={columnCount} style={{ padding: 0, verticalAlign: 'top', borderBottom: '1px solid #e0e0e0' }}>
                                                {rowLoading ? (
                                                    <div style={{ padding: 24, textAlign: 'center', color: '#757575' }}>Loading...</div>
                                                ) : (
                                                    rowContent
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })
                    }
                    </tbody>
                </table>
            </div>
            {renderPagination()}
        </Fragment>

    );
};

export default memo(ListTable);
